import { useMemo } from "react";

import { useResponsive } from "../../../../../hooks/useResponsive";
import { getNumberFormat, formatSignedPercent } from "../../../../../utils";
import { BREAKPOINTS } from "../../../../../utils/constants";

import { Sparkline } from "../../../../../components/Sparkline";
import { SortableColumnHeader } from "../../../../../components/SortableColumnHeader";
import { ValuationBadge } from "../../../../../components/Valuation";
import { buildHoldings, sortHoldings, sparklineOf } from "./holdings";
import {
  BodyRow,
  Card,
  CardGain,
  CardList,
  CardTop,
  CardTrendBox,
  CardValue,
  CardValueRow,
  Cell,
  Dot,
  GainText,
  HeaderCell,
  HeaderRow,
  InvestedText,
  NameButton,
  NameText,
  PriceUpdatedNote,
  ReturnChip,
  Section,
  SinceFooter,
  SinceText,
  Table,
  TableCard,
  TableScroll,
  TrendBox,
  TrendCell,
  TypeLabel,
  TypeRow,
  ValuationNote,
  ValuationRow,
  ValueStack,
  ValueText,
} from "./styles";
import { InvestmentsHoldingsProps, SortKey } from "./types";

const NUMERIC_COLUMNS: { key: SortKey; label: string }[] = [
  { key: "value", label: "Value" },
  { key: "invested", label: "Invested" },
  { key: "gain", label: "Gain" },
  { key: "return", label: "Return" },
];

const formatSignedCurrency = (value: number | null): string => {
  if (value === null) {
    return "—";
  }
  const amount = `${getNumberFormat(Math.abs(value))} €`;
  if (value > 0) {
    return `+${amount}`;
  }
  if (value < 0) {
    return `-${amount}`;
  }
  return amount;
};

export const InvestmentsHoldings: React.FC<InvestmentsHoldingsProps> = ({
  investments,
  byInvestment,
  onSelect,
  sort,
  onSortChange,
}) => {
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);

  const trendById = useMemo(
    () =>
      new Map(
        byInvestment.map((series) => [
          series.investmentId,
          sparklineOf(series),
        ]),
      ),
    [byInvestment],
  );

  const baseRows = useMemo(
    () => buildHoldings(investments, trendById),
    [investments, trendById],
  );
  const rows = useMemo(() => sortHoldings(baseRows, sort), [baseRows, sort]);

  const handleSort = (key: SortKey) => {
    if (sort.key === key) {
      onSortChange({ key, dir: sort.dir === "asc" ? "desc" : "asc" });
      return;
    }
    onSortChange({ key, dir: "desc" });
  };

  // The name button is the keyboard target; stop the click from also reaching
  // the row's mouse handler so a click on the name navigates only once.
  const handleNameClick = (event: React.MouseEvent, id: number) => {
    event.stopPropagation();
    onSelect(id);
  };

  const handleCardKeyDown = (event: React.KeyboardEvent, id: number) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(id);
    }
  };

  if (isCompact) {
    return (
      <Section>
        <CardList>
          {rows.map((row) => (
            <Card
              key={row.id}
              role="button"
              tabIndex={0}
              aria-label={`View ${row.name}`}
              onClick={() => onSelect(row.id)}
              onKeyDown={(event) => handleCardKeyDown(event, row.id)}
            >
              <CardTop>
                <NameText>{row.name}</NameText>
                {row.value !== null && (
                  <ReturnChip $tone={row.returnTone}>
                    {formatSignedPercent(row.returnPercent)}
                  </ReturnChip>
                )}
              </CardTop>
              <TypeRow>
                <Dot $color={row.typeColor} />
                <TypeLabel>{row.typeName}</TypeLabel>
              </TypeRow>
              <CardValueRow>
                <CardValue>{row.valueText}</CardValue>
                <CardGain $tone={row.gainTone}>
                  {formatSignedCurrency(row.gain)}
                </CardGain>
              </CardValueRow>
              {row.badge && (
                <ValuationRow>
                  <ValuationBadge badge={row.badge} hasVisibleNote={true} />
                  <ValuationNote>{row.badge.message}</ValuationNote>
                </ValuationRow>
              )}
              <PriceUpdatedNote priceUpdated={row.priceUpdated} />
              {row.value !== null && row.trend.length > 0 && (
                <CardTrendBox>
                  <Sparkline data={row.trend} tone={row.gainTone} />
                </CardTrendBox>
              )}
              <SinceFooter>Since {row.since}</SinceFooter>
            </Card>
          ))}
        </CardList>
      </Section>
    );
  }

  return (
    <Section>
      <TableCard>
        <TableScroll>
          <Table>
            <thead>
              <HeaderRow>
                <HeaderCell $align="left">Investment</HeaderCell>
                <SortableColumnHeader
                  column="since"
                  label="Since"
                  align="start"
                  sort={sort}
                  onSort={handleSort}
                />
                {NUMERIC_COLUMNS.map((column) => (
                  <SortableColumnHeader
                    key={column.key}
                    column={column.key}
                    label={column.label}
                    align="end"
                    sort={sort}
                    onSort={handleSort}
                  />
                ))}
                <HeaderCell $align="right">Trend</HeaderCell>
              </HeaderRow>
            </thead>
            <tbody>
              {rows.map((row) => (
                <BodyRow key={row.id} onClick={() => onSelect(row.id)}>
                  <Cell $align="left">
                    <NameButton
                      type="button"
                      onClick={(event) => handleNameClick(event, row.id)}
                    >
                      {row.name}
                    </NameButton>
                    <TypeRow>
                      <Dot $color={row.typeColor} />
                      <TypeLabel>{row.typeName}</TypeLabel>
                    </TypeRow>
                  </Cell>
                  <Cell $align="left">
                    <SinceText>{row.since}</SinceText>
                  </Cell>
                  <Cell $align="right">
                    <ValueStack>
                      <ValueText>{row.valueText}</ValueText>
                      {/* No visible note fits this column, so the badge is the
                          only place the sentence can live here. */}
                      {row.badge && <ValuationBadge badge={row.badge} />}
                      <PriceUpdatedNote priceUpdated={row.priceUpdated} />
                    </ValueStack>
                  </Cell>
                  <Cell $align="right">
                    <InvestedText>
                      {getNumberFormat(row.invested)} €
                    </InvestedText>
                  </Cell>
                  <Cell $align="right">
                    <GainText $tone={row.gainTone}>
                      {formatSignedCurrency(row.gain)}
                    </GainText>
                  </Cell>
                  {/* Return is derived from current/invested/withdrawn values, not the history series, so an empty trend isn't a reason to hide it (same rule as the mobile card). */}
                  <Cell $align="right">
                    {row.value === null ? (
                      "—"
                    ) : (
                      <ReturnChip $tone={row.returnTone}>
                        {formatSignedPercent(row.returnPercent)}
                      </ReturnChip>
                    )}
                  </Cell>
                  {/* An empty series is a dash, not an empty box: `Sparkline` renders nothing for
                      no data, which would leave the column silently blank. */}
                  <TrendCell>
                    {row.value === null || row.trend.length === 0 ? (
                      "—"
                    ) : (
                      <TrendBox>
                        <Sparkline data={row.trend} tone={row.gainTone} />
                      </TrendBox>
                    )}
                  </TrendCell>
                </BodyRow>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      </TableCard>
    </Section>
  );
};
